import map from "lodash/map";
import throttle from "lodash/throttle";
import { useAppLayoutStore } from "PFApp/stores/app_layout_store";
import { Card } from "PFComponents/card";
import { Tabs, TabsVariant } from "PFComponents/tabs";
import { Color } from "PFCore/types";
import { IconName } from "PFTheme/graphics/icons";
import { useEffect, useRef, useState } from "react";
import { useMemo } from "react";

import {
  useActivityFormStore,
  useActivityFormStoreActions
} from "../../activity_form_store/use_activity_form_store";
import { useSectionsUtils } from "../../hooks/use_sections_utils";
import { useVisibleSections } from "../../hooks/use_visible_sections";
import css from "./navigation.module.scss";
import { getNavigationOffsetStyles } from "./navigation.utils";

type NavigationProps = {
  isSmallScreen: boolean;
  isEngagementBarVisible: boolean;
};

export const Navigation = ({ isSmallScreen, isEngagementBarVisible }: NavigationProps) => {
  const appLeftPadding = useAppLayoutStore((state) => state.mainLeft);
  const appRightPadding = useAppLayoutStore((state) => state.mainRight);
  const currentSectionId = useActivityFormStore((state) => state.currentSectionId);
  const { hasSectionErrors } = useSectionsUtils();
  const visibleSections = useVisibleSections();
  const { setCurrentSectionId } = useActivityFormStoreActions();
  const stickyRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!stickyRef.current) {
        return;
      }
      const { top } = stickyRef.current.getBoundingClientRect();
      setIsSticky(top <= 0);
    }, 100);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setCurrentSectionId(visibleSections[0].id);
  }, []);

  const handleChange = (id: string) => setCurrentSectionId(id);

  const tabs = useMemo(
    () =>
      map(visibleSections, ({ id, title, count }) => ({
        id,
        title,
        count,
        rightIcon: hasSectionErrors(id) ? ("error" as IconName) : undefined,
        rightIconColor: "paletteRed1" as Color,
        spread: true
      })),
    [visibleSections, hasSectionErrors]
  );

  const isFullSize = isSmallScreen && isSticky;
  const styles = getNavigationOffsetStyles({
    isEngagementBarVisible,
    isFullSize,
    isSmallScreen,
    appLeftPadding,
    appRightPadding
  });

  return (
    <Card
      className={css.navigation}
      style={styles}
      shadowVariant={isFullSize ? "default" : "none"}
      ref={stickyRef}
    >
      <Tabs
        tabs={tabs}
        variant={isSmallScreen ? TabsVariant.Horizontal : TabsVariant.Vertical}
        onChange={handleChange}
        activeTab={currentSectionId}
        wrap
      />
    </Card>
  );
};
